import { useMutation } from '@apollo/client';
import { faArrowRight, faPen } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCadenceCompact, fromToolArgs } from './automations/scheduleFormat.ts';
import { ChatAlert } from './ChatAlert.tsx';
import {
    CREATE_SCHEDULE_TOOL,
    getToolIcon,
    getToolMetaKey,
    humanizeToolName,
    toReadableArgKey,
} from './toolDisplay.tsx';
import Button from '../../core/components/Button.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import TimeField from '../../core/components/Fields/TimeField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import SegmentedButton from '../../core/components/SegmentedButton.tsx';
import { CANCEL_AGENT_TOOL_CALL, CONFIRM_AGENT_TOOL_CALL, GET_CONVERSATION_SCHEDULES } from '../queries.ts';

const SEND_EMAIL_TOOL = 'SendEmailFromChatToolClass';
const NameEditIcon = withIcon(faPen);
const AdjustIcon = withIcon(faArrowRight);

const formatValue = (value, t) => {
    if (Array.isArray(value)) return t('pendingAction.items', { count: value.length });
    if (typeof value === 'object' && value !== null) return JSON.stringify(value);
    return String(value);
};

const ArgsSummary = ({ args }) => {
    const { t } = useTranslation('chat');
    const entries = Object.entries(args).filter(
        ([, value]) => value !== '' && value !== null && !(Array.isArray(value) && value.length === 0)
    );
    if (entries.length === 0) return null;
    return (
        <div className="flex flex-col gap-0.5 text-xs text-content-medium">
            {entries.map(([key, value]) => (
                <div key={key} className="flex gap-2">
                    <span className="font-semibold">{t(`pendingAction.fields.${key}`, toReadableArgKey(key))}</span>
                    <span className="min-w-0 break-words">{formatValue(value, t)}</span>
                </div>
            ))}
        </div>
    );
};

const EmailSummary = ({ args }) => {
    const { t } = useTranslation('chat');
    const recipients = (args.to_recipients || []).join(', ');
    const attachments = (args.attachment_file_names || []).join(', ');
    return (
        <div className="flex flex-col gap-1 text-xs text-content-medium">
            {args.reply_to_message_id ? (
                <div>{t('pendingAction.email.reply')}</div>
            ) : (
                <div>
                    <span className="font-semibold">{t('pendingAction.email.to')}: </span>
                    {recipients}
                </div>
            )}
            {args.subject && (
                <div>
                    <span className="font-semibold">{t('pendingAction.email.subject')}: </span>
                    {args.subject}
                </div>
            )}
            <div className="mt-1 whitespace-pre-wrap rounded-lg bg-surface-strong px-2.5 py-2 text-content-strong">
                {args.body}
            </div>
            <p className="m-0 text-xs text-content-weak">{t('pendingAction.email.aiGenerated')}</p>
            {attachments && (
                <div>
                    <span className="font-semibold">{t('pendingAction.email.attachments')}: </span>
                    {attachments}
                </div>
            )}
            <div className="font-medium text-content-warning-strong">
                {args.mode === 'send' ? t('pendingAction.email.modeSend') : t('pendingAction.email.modeDraft')}
            </div>
        </div>
    );
};

// the cadence settings the proposal card lets the user adjust in place; everything else goes
// through "Adjust details" (the full dialog)
type ScheduleDraft = {
    name: string;
    frequency: string;
    time_of_day: string | null;
    weekday: number | null;
    day_of_month: number | null;
    interval_minutes: number | null;
    interval_unit: string | null;
};

const DRAFT_FIELDS = Object.keys({
    name: 1,
    frequency: 1,
    time_of_day: 1,
    weekday: 1,
    day_of_month: 1,
    interval_minutes: 1,
    interval_unit: 1,
}) as (keyof ScheduleDraft)[];

const SCHEDULE_FREQUENCIES = ['daily', 'weekly', 'monthly'];

const draftFromArgs = (args): ScheduleDraft => ({
    name: args.name || '',
    frequency: args.frequency || 'daily',
    time_of_day: args.time_of_day ? String(args.time_of_day).slice(0, 5) : null,
    weekday: args.weekday ?? null,
    day_of_month: args.day_of_month ?? null,
    interval_minutes: args.interval_minutes ?? null,
    interval_unit: args.interval_unit ?? null,
});

const withFrequency = (draft: ScheduleDraft, frequency: string): ScheduleDraft => ({
    ...draft,
    frequency,
    time_of_day: draft.time_of_day || '08:00',
    weekday: frequency === 'weekly' ? (draft.weekday ?? 0) : null,
    day_of_month: frequency === 'monthly' ? (draft.day_of_month ?? 1) : null,
    interval_minutes: null,
    interval_unit: null,
});

const SECTION_LABEL_CLASS = 'text-xs font-medium uppercase tracking-wide text-content-weak';
const FOCUS_RING_CLASS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong';

const ScheduleSummary = ({ args, draft, onChange }) => {
    const { t } = useTranslation('chat');
    const [editingName, setEditingName] = useState(false);
    const cadence = [
        formatCadenceCompact(t, fromToolArgs(draft)),
        args.timezone,
        args.ends_on && t('pendingAction.schedule.until', { date: args.ends_on }),
    ]
        .filter(Boolean)
        .join(' · ');
    return (
        <div className="flex flex-col gap-2 text-xs text-content-medium">
            <div className="flex items-center gap-2">
                {editingName ? (
                    <StringField
                        autoFocus
                        className="max-w-xs"
                        value={draft.name}
                        onValueChange={(name) => onChange({ ...draft, name })}
                        onBlur={() => setEditingName(false)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false);
                        }}
                    />
                ) : (
                    <>
                        <span className="text-sm font-medium text-content-strong">{draft.name}</span>
                        <button
                            type="button"
                            aria-label={t('pendingAction.schedule.editName')}
                            onClick={() => setEditingName(true)}
                            className={`cursor-pointer rounded border-none bg-transparent p-0.5 text-content-weak hover:text-content-strong ${FOCUS_RING_CLASS}`}
                        >
                            <NameEditIcon />
                        </button>
                    </>
                )}
            </div>
            <div className="flex flex-col gap-1">
                <span className={SECTION_LABEL_CLASS}>{t('pendingAction.schedule.every')}</span>
                <div className="flex flex-wrap items-center gap-2">
                    <SegmentedButton
                        variant="joined"
                        value={SCHEDULE_FREQUENCIES.includes(draft.frequency) ? draft.frequency : ''}
                        onValueChange={(frequency) => onChange(withFrequency(draft, frequency))}
                    >
                        {SCHEDULE_FREQUENCIES.map((frequency) => (
                            <SegmentedButton.Item key={frequency} value={frequency}>
                                {t(`automations.dialog.frequencies.${frequency}`)}
                            </SegmentedButton.Item>
                        ))}
                    </SegmentedButton>
                    {draft.time_of_day !== null && (
                        <TimeField
                            className="w-28"
                            aria-label={t('automations.dialog.at')}
                            value={draft.time_of_day}
                            onValueChange={(timeOfDay) => onChange({ ...draft, time_of_day: timeOfDay || '08:00' })}
                        />
                    )}
                </div>
                <span>{cadence}</span>
            </div>
            <div className="flex flex-col gap-1">
                <span className={SECTION_LABEL_CLASS}>{t('pendingAction.schedule.deliverTo')}</span>
                <span>{t('pendingAction.schedule.conversation')}</span>
            </div>
            {args.prompt && (
                <div className="mt-1 whitespace-pre-wrap rounded-lg bg-surface-strong px-2.5 py-2 text-content-strong">
                    {args.prompt}
                </div>
            )}
        </div>
    );
};

export const PendingActionCard = ({ messageId, toolCall, onAdjustSchedule = null }) => {
    const { t } = useTranslation('chat');
    const [failed, setFailed] = useState(false);
    const [confirmToolCall, { loading: confirming }] = useMutation(CONFIRM_AGENT_TOOL_CALL);
    const [cancelToolCall, { loading: cancelling }] = useMutation(CANCEL_AGENT_TOOL_CALL);
    const busy = confirming || cancelling;

    const toolName = toolCall.tool_name || '';
    const metaKey = getToolMetaKey(toolName);
    const label = metaKey ? t(`new.tools.${metaKey}.label`) : humanizeToolName(toolName);
    const ToolIcon = getToolIcon(toolName);
    const args = toolCall.tool_input || {};
    const isSchedule = toolName === CREATE_SCHEDULE_TOOL;
    const [draft, setDraft] = useState<ScheduleDraft>(() => draftFromArgs(args));

    const run = async (mutate, options = {}) => {
        setFailed(false);
        try {
            await mutate({ variables: { messageId }, ...options });
        } catch (e) {
            console.error(e);
            setFailed(true);
        }
    };

    // only the settings the user changed ride along as the confirm payload; the backend merges
    // them into the proposed call before creating the schedule
    const adjustedFields = () => {
        const original = draftFromArgs(args);
        const changed: Partial<ScheduleDraft> = {};
        if (draft.name.trim() && draft.name !== original.name) changed.name = draft.name.trim();
        if (DRAFT_FIELDS.some((field) => field !== 'name' && draft[field] !== original[field])) {
            Object.assign(changed, { ...draft, name: changed.name ?? original.name });
        }
        return Object.keys(changed).length > 0 ? changed : null;
    };

    const confirmSchedule = () => {
        const changes = adjustedFields();
        run(confirmToolCall, {
            variables: { messageId, payload: changes ? JSON.stringify(changes) : undefined },
            // a confirmed schedule must show up in the automations list and the thread header right away
            refetchQueries: [{ query: GET_CONVERSATION_SCHEDULES }],
        });
    };

    // the full dialog opens prefilled with the (possibly already adjusted) settings; the proposal
    // card stays as the fallback — the dialog cancels the pending call only once it saves
    const adjustDetails = () => onAdjustSchedule?.({ ...args, ...draft }, messageId);

    return (
        <div className="mt-1.5 flex w-full flex-col gap-2.5 rounded-xl border border-solid border-line-brand-default bg-surface-weak px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-content-strong">
                <ToolIcon className="flex-none text-content-medium" />
                {t(isSchedule ? 'pendingAction.schedule.title' : 'pendingAction.title', { tool: label })}
            </div>
            {isSchedule ? (
                <ScheduleSummary args={args} draft={draft} onChange={setDraft} />
            ) : toolName === SEND_EMAIL_TOOL ? (
                <EmailSummary args={args} />
            ) : (
                <ArgsSummary args={args} />
            )}
            {failed && (
                <ChatAlert
                    severity="warning"
                    title={t('pendingAction.error.title')}
                    text={t('pendingAction.error.text')}
                />
            )}
            {isSchedule ? (
                <div className="flex items-center justify-between gap-2">
                    {onAdjustSchedule ? (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={adjustDetails}
                            className={`flex cursor-pointer items-center gap-1.5 rounded border-none bg-transparent p-0.5 text-sm font-medium text-content-brand-strong disabled:opacity-50 ${FOCUS_RING_CLASS}`}
                        >
                            {t('pendingAction.schedule.adjustDetails')}
                            <AdjustIcon />
                        </button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button variant="ghost" className="!px-4" disabled={busy} onClick={() => run(cancelToolCall)}>
                            {t('pendingAction.schedule.notNow')}
                        </Button>
                        <Button variant="primary" className="!px-4" disabled={busy} onClick={confirmSchedule}>
                            {t('pendingAction.schedule.confirm')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-2">
                    <Button variant="primary" className="!px-4" disabled={busy} onClick={() => run(confirmToolCall)}>
                        {t(failed ? 'pendingAction.tryAgain' : 'pendingAction.confirm')}
                    </Button>
                    <Button variant="outline" className="!px-4" disabled={busy} onClick={() => run(cancelToolCall)}>
                        {t('pendingAction.cancel')}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PendingActionCard;
